import { useRef } from "react";

/**
 * Returns a new object with only the entries where values are not `null` or `undefined`.
 * Useful for conditionally spreading props into objects without verbose null checks.
 *
 * @example
 * ```typescript
 * const config = pickDefined({
 *   enabled: true,
 *   value: undefined,
 *   count: null,
 *   name: "test",
 * });
 * // Result: { enabled: true, name: "test" }
 * ```
 */
export function pickDefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== null && value !== undefined),
  ) as Partial<T>;
}

/**
 * Hook that returns a ref which always holds the latest value.
 * Useful for stabilizing callbacks passed to ag-grid while ensuring they
 * always reference the latest consumer callback.
 */
export function useLatestRef<T>(value: T): React.MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
